import { addDays, format, parseISO, subDays } from 'date-fns';
import { isMigratedLineageTerm, isOriginalImportedTerm } from '@ourbranch/policy-utils';
import {
  policyType,
  renewalGenerationBixOnlyStates,
  renewalGenerationBlocks,
  renewalGenerationDaysBeforeEndDate
} from '@ourbranch/lookups';
import { appConfig } from 'brand-config';

// Home, condo and renters share one window in the renewals lookup. Farm has no entry (different renewal flow).
const renewalLookupPolicyType = {
  [policyType.Auto]: policyType.Auto,
  [policyType.Home]: policyType.Home,
  [policyType.Condo]: policyType.Home,
  [policyType.Renters]: policyType.Home
};

/**
 * The window of a brand on the windowed renewals cron (brand config automatedRenewalWindow, mirrored
 * from brand-config-be): every policy type, farm included, counted toward the renewal's effective date
 * (endDate + 1). Null before the brand's go-live floor, where the legacy carrier still renews. (#36919)
 * An imported lineage's window starts no earlier than the brand's imported-policy automation start date,
 * before which the cron leaves imported policies alone; natively bound policies keep theirs. (#37225)
 */
const getBrandRenewalGenerationWindow = (policy, brandWindow, importedPolicyAutomationStartDate) => {
  if (
    !policy?.endDate ||
    policy.autoRenew === false ||
    policy.endDate !== (policy.fullTermPolicyEndDate || policy.endDate)
  ) {
    return null;
  }
  const renewalEffectiveDate = addDays(parseISO(policy.endDate), 1);
  if (
    brandWindow.renewalEffectiveOnOrAfter &&
    format(renewalEffectiveDate, 'yyyy-MM-dd') < brandWindow.renewalEffectiveOnOrAfter
  ) {
    return null;
  }
  const maxDaysBeforeRenewal = isOriginalImportedTerm(policy)
    ? brandWindow.importedTermMaxDaysBeforeRenewal
    : brandWindow.maxDaysBeforeRenewal;
  const from = format(subDays(renewalEffectiveDate, maxDaysBeforeRenewal), 'yyyy-MM-dd');
  const startDate = isMigratedLineageTerm(policy) ? importedPolicyAutomationStartDate : undefined;
  return {
    // yyyy-MM-dd strings compare in date order
    from: startDate && from < startDate ? startDate : from,
    to: format(subDays(renewalEffectiveDate, brandWindow.minDaysBeforeRenewal), 'yyyy-MM-dd')
  };
};

/**
 * Dates the renewals cron will try to generate this policy's renewal, as yyyy-MM-dd { from, to }:
 * `from` is the first day it is picked up, `to` the last day a failed attempt is retried. (#34196)
 * Null when no renewal is coming: auto-renew off, a cancelled term, or a state or carrier exit the cron skips.
 * A brand with its own renewal window (brand config automatedRenewalWindow) uses that for every policy
 * type, farm included (#36919); without one, Branch's state lookup decides and farm gets no window.
 * Does not check whether the renewal already exists; the caller shows a link to it instead.
 */
export const getRenewalGenerationWindow = (
  policy,
  brandWindow = appConfig?.automatedRenewalWindow,
  importedPolicyAutomationStartDate = appConfig?.importedPolicyAutomationStartDate
) => {
  if (brandWindow) {
    return getBrandRenewalGenerationWindow(policy, brandWindow, importedPolicyAutomationStartDate);
  }
  const lookupPolicyType = renewalLookupPolicyType[policy?.policyType];
  // The renewals model renews WV from the 4th term on even with auto-renew off.
  const renewsAutomatically = policy?.autoRenew !== false || (policy?.state === 'WV' && policy?.term >= 4);
  if (
    !policy?.endDate ||
    !lookupPolicyType ||
    !renewsAutomatically ||
    (renewalGenerationBixOnlyStates.includes(policy.state) && !policy.isBix) ||
    renewalGenerationBlocks.some(
      (block) =>
        block.states.includes(policy.state) &&
        (!block.policyTypes || block.policyTypes.includes(policy.policyType)) &&
        policy.endDate >= block.endDateOnOrAfter // yyyy-MM-dd strings compare in date order
    ) ||
    // fullTermPolicyEndDate can be null; fall back to endDate like getPolicyStatus does.
    policy.endDate !== (policy.fullTermPolicyEndDate || policy.endDate)
  ) {
    return null;
  }

  const { first, last } = (renewalGenerationDaysBeforeEndDate[policy.state] ??
    renewalGenerationDaysBeforeEndDate.DEFAULT)[lookupPolicyType];
  const endDate = parseISO(policy.endDate);
  return {
    from: format(subDays(endDate, first), 'yyyy-MM-dd'),
    to: format(subDays(endDate, last), 'yyyy-MM-dd')
  };
};
